import {StaticPageTitle} from '@common/seo/static-page-title';
import {Trans} from '@ui/i18n/trans';
import {useLibraryStore} from '@app/web-player/library/state/likes-store';
import React, { useMemo } from 'react';
import {TextField} from '@ui/forms/input-field/text-field/text-field';
import {SearchIcon} from '@ui/icons/material/Search';
import {message} from '@ui/i18n/message';
import {useTrans} from '@ui/i18n/use-trans';
import {PageErrorMessage} from '@common/errors/page-error-message';
import {AlbumGridItem} from '@app/web-player/albums/album-grid-item';
import {ContentGrid} from '@app/web-player/playable-item/content-grid';
import {InfiniteScrollSentinel} from '@common/ui/infinite-scroll/infinite-scroll-sentinel';
import {AnimatePresence, m} from 'framer-motion';
import {opacityAnimation} from '@ui/animation/opacity-animation';
import {MediaPageNoResultsMessage} from '@app/web-player/layout/media-page-no-results-message';
import {PlayableMediaGridSkeleton} from '@app/web-player/playable-item/player-media-grid-skeleton';
import {AdHost} from '@common/admin/ads/ad-host';
import clsx from 'clsx';
import { isMobile } from '@common/core/platform-detection';
import { NotificationDialogTrigger } from '@common/notifications/dialog/notification-dialog-trigger';
import { Button } from '@ui/buttons/button';
import { useUserLiveEventTickets } from './requests/use-user-live-event-tickets';
import { useIsMobileMediaQuery } from '@ui/utils/hooks/is-mobile-media-query';
import { useNavigate } from 'react-router-dom';
import TicketList, { Ticket } from '@app/web-player/live-events/live-event-tickets-list';
import { LiveEventTicket } from '@app/admin/live-events/interfaces/live-event-ticket';

const sortItems = {
  'likes.created_at:desc': message('Recently added'),
  'name:asc': message('A-Z'),
  'release_date:desc': message('Release date'),
};

export function LibraryTicketsPage() {
  const {trans} = useTrans();
  const navigate = useNavigate();
  const query = useUserLiveEventTickets('me', {willSortOrFilter: true});
  const {
    isLoading,
    isError,
    items
  } = query;

  if (isError) {
    return <PageErrorMessage />;
  }

  const tickets: Ticket[] = useMemo(
    () =>
      items.map((item) => ({
        id: item.ticket?.id,
        name: item.ticket?.name ?? "Ticket",
        eventName: item.ticket?.live_event?.name ?? "Évènement",
        participant_name: item.participant_name!,
        participant_extra_data: item.participant_extra_data ?? [],
        starts_at: item.ticket?.live_event?.starts_at ?? undefined,
        ends_at: item.ticket?.live_event?.ends_at ?? undefined,
        scanSessions: item.ticket?.scan_sessions ?? [],
        uuid: item.uuid,
      })),
    [items],
  );

  return (
    <div>
      <StaticPageTitle>
        <Trans message="Your tickets" />
      </StaticPageTitle>
      <AdHost slot="general_top" className="mb-34" />
      <div className={clsx('mb-30 flex items-center justify-between gap-24')} >
        <h1 className="flex-auto truncate whitespace-nowrap overflow-hidden text-3xl font-semibold">
          <Trans message="My tickets" />
        </h1>
        {isMobile() ? <NotificationDialogTrigger /> : null}
      </div>
      
      <div className="mt-34">
        <AnimatePresence initial={false} mode="wait">
          {isLoading ? (
            <PlayableMediaGridSkeleton itemCount={isMobile() ? 6 : 4} />
          ) : (
            <m.div key="media-grid" {...opacityAnimation}>
              <ContentGrid fullWidht={true}>
                <TicketList tickets={tickets} />
                <InfiniteScrollSentinel query={query} />
              </ContentGrid>
            </m.div>
          )}
        </AnimatePresence>
      </div>
      {!items.length && !isLoading && (
        <MediaPageNoResultsMessage
          className="mt-34"
          description={
            <div>
                <div><Trans message="You have not added any tickets to your library yet." /></div>
                <div>
                  <Button variant="outline" onClick={() => navigate("/upcoming-events")} className="mt-16">
                    <Trans message="Browse events" />
                  </Button>
                </div>
            </div>
          }
        />
      )}
      <AdHost slot="general_bottom" className="mt-34" />
    </div>
  );
}
